import { CircularProgress, Stack } from "@mui/material";
import FileTreeView from "../../data-wrangler/components/AdvanceDataWrangler/FileTreeView";
import React, { useMemo } from "react";
import { getTabular } from "../../data-wrangler/UtilityFunctions";
import { useSelector } from "react-redux";
import { selectSelectedDatabase } from "../../common/databaseSlice";
import { useWizardQuery } from "../../../api/dataWranglerApiSlice";


type props = {
    study: string;
};

const TabularFiles = ({ study }: props) => {
    const selectedDatabase = useSelector(selectSelectedDatabase);
    const { data, isLoading } = useWizardQuery({ database: selectedDatabase });
    const treeUIIDs = new Set<string>();

    const getChildrenTree = ((node: any, id: string, selectionPath: string[]) : any => {
        const nodeCommon: any = {};
        // Check that label is not a group
        if (node.label && typeof node.label == 'string') {
            nodeCommon.humanReadableLabel = node.label;
        }
        // if node.source_suffix is NULL, description applies to the first level group
        // if typeof node.description is not a string, description is a group
        if (
            node.description
            && node.source_suffix !== null
            && typeof node.description == 'string'
        ) {
            nodeCommon.description = node.description;
        }

        if ('suffix' in node) {
            const suffix = node.virtual_suffix || node.suffix || "[no_suffix]";
            const groups = (node.groups || []).map((group: string) => `group::${group}`);
            const nodeId = `${id}__${suffix}`;

            if (!treeUIIDs.has(nodeId)) {
                treeUIIDs.add(nodeId);
                return {
                    ...nodeCommon,
                    id: nodeId,
                    selectionPath: [...selectionPath, ...groups, `suffix::${node.suffix || "[no_suffix]"}`],
                    type: 'file_suffix',
                    groups: node.groups,
                    formats: node.format_options,
                    label: suffix,
                    parentId: id,
                };
            }
        } else {
            return Object.keys(node || {})
                .filter((label) => {
                    if (Number.isInteger(parseInt(label))) return true;
                    const nodeId = id ? `${id}__${label}` : label;
                    if (!treeUIIDs.has(nodeId)) {
                        treeUIIDs.add(nodeId);
                        return true;
                    }
                    return false;
                })
                .map((label: any) => {
                    if (!isNaN(label)) {
                        return getChildrenTree(node[label], id, selectionPath);
                    } else if (label !== 'description') {
                        const nodeId = id ? `${id}__${label}` : label;
                        const tree: any = {
                            ...nodeCommon,
                            id: nodeId,
                            label: label,
                            parentId: id,
                            description: node[label]?.description,
                        }

                        // if any direct child with display_folder_only
                        const firstChild = node[label]?.[0];
                        if (firstChild?.display_folder_only) {
                            const groups = (firstChild.groups || []).map((group: string) => `group::${group}`);
                            tree.selectionPath = [
                                ...selectionPath,
                                ...groups,
                                ...(firstChild.format_options ? [`extension::${firstChild.format_options}`] : [])
                            ];
                        } else {
                            const children = getChildrenTree(node[label], nodeId, selectionPath).flat().filter(Boolean);
                            if (children.length > 0) {
                                tree.children = children;
                            }
                        }
                        return tree;
                    }
                });
        }
    });

    const getFileData = (fileType: string, dataset: string) => {
        if (!data?.[fileType]) return [];
        const files: any = getTabular(data?.[fileType], dataset);
        const tabularFilterConfig = data?.[fileType]
            .filter((node: any) => node.file_dataset === dataset && node.format_options.includes('parquet'))
            .length > 0 ? { selectionPath: [dataset, fileType] } : null;
        return {
            tabularFilterConfig: tabularFilterConfig,
            options: getChildrenTree(files, `${dataset}_${fileType}`, [dataset, fileType])
        };
    }

    const tabularDatasetsOptions = useMemo(() => {
        const options: any = {};
        data?.file_datasets.forEach(({ name: dataset }: {name: string}) =>
            options[dataset] = getFileData('files_tabular', dataset)
        );
        return options;
    }, [data]);

    return (
        <Stack>
            {isLoading && <CircularProgress/>}
            {!isLoading &&
                <FileTreeView
                    options={tabularDatasetsOptions?.[study]?.options}
                    tabularFilter={tabularDatasetsOptions?.[study]?.tabularFilterConfig}
                />}
        </Stack>
    )
}

export default TabularFiles;